import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import { IAssignment } from '@/components/interfaces';
import useGradesTableStore from '../store/useGradesTableStore';

const useAssignments = () => {
  const stafferId = useGradesTableStore(state => state.stafferId);
  const programId = useGradesTableStore(state => state.programId);
  const allLessons = useGradesTableStore(state => state.allLessons);
  const lesson = useGradesTableStore(state => state.lesson);
  const unit = useGradesTableStore(state => state.unit);
  const classroom = useGradesTableStore(state => state.classroom);
  const setStore = useGradesTableStore(state => state.setStore);

  const query = useQuery(['Assignment', stafferId, allLessons ? 'all' : lesson?.id ?? unit?.id], () => {
    setStore({ isLoadingAssignments: true });

    const searchParams = {
      extra_attributes: ['assignment_details', 'investigation'],
      for_classroom: classroom.id,
      in_program: programId,
      includes: ['lms_provider', { assignments_models: ['unit', 'lesson'] }],
      only: [
        'assignment_details',
        'assignment_models_hash',
        'can_clear_exercise_results',
        'due_datetime',
        'first_lesson_id',
        'id',
        'lms_provider',
        'lms_type',
        'max_points',
        'normalization_factor',
        'connected_to_google',
        'connected_to_schoology',
        'connected_to_canvas',
        'scoring_mode',
      ],
      order: ['due_datetime ASC'],
    };

    if (!allLessons) {
      if (lesson) {
        searchParams['in_lesson'] = lesson.id;
      } else {
        searchParams['in_unit'] = unit.id;
      }
    }

    return axios.get(Routes.plato_api_assignments_path(searchParams))
      .then(res => (
        res.data.data.sort((a: IAssignment, b: IAssignment) => {
          // sort by due date ASC, assignment type ASC, assignment title ASC
          const dueDateA = new Date(a.due_datetime);
          const dueDateB = new Date(b.due_datetime);
          if (dueDateA.valueOf() !== dueDateB.valueOf()) {
            return dueDateA.valueOf() - dueDateB.valueOf();
          }

          if (a.assignment_details.type !== b.assignment_details.type) {
            return a.assignment_details.type.localeCompare(b.assignment_details.type);
          }

          return a.assignment_details.title.localeCompare(b.assignment_details.title);
        })
      ))
      .finally(() => setStore({ isLoadingAssignments: false }));
  });

  useEffect(() => {
    if (query.isFetching || !query.data) return;

    setStore({ assignments: query.data });
  }, [query.data]);

  return query;
};

export default useAssignments;
